/**
 * Składanie arkuszy teczki w jeden PDF: rzut dachu, arkusze połaci, schemat instalacji, rozmieszczenie urządzeń
 * (i kolejne). Każdy opis arkusza ma `uklad` (z `ukladArkusza`), `tytul`, `skala` (liczba albo null), `legenda`,
 * `opis` ({ naglowek, wiersze }), opcjonalnie łańcuchy wymiarów (`lancuchyX`, `lancuchyY` z `rozmiescArkusz`)
 * i funkcję `rysujTresc(r)`, która rysuje właściwy rysunek rysownikiem z `pdf.js`.
 *
 * Teczka łączy arkusze z kilku źródeł: `ponumerujArkusze([...dach.arkusze, ...instalacja.arkusze], opcje)`
 * i `narysujArkusze({ tytul, arkusze }, ...)`. Tabelka każdego arkusza dostaje wtedy wspólną numerację.
 */
import { dataRysunku } from './arkusz'
import { PT, rysownik, rysujRamke, rysujTabelke, rysujLegende, rysujOpis, rysujLancuch } from './pdf'
import { DOPISEK_RYSUNKU } from '../../config/rysunki'

/** Tabelki z numeracją „ark. i z n”, datą, numerem teczki i dopiskiem. Zmienia i zwraca listę. */
export const ponumerujArkusze = (arkusze, { data = dataRysunku(), nrTeczki = '', dopisek = DOPISEK_RYSUNKU } = {}) => {
  arkusze.forEach((a, i) => {
    a.tabelka = {
      dopisek, tytul: a.tytul, skala: a.skala ? `1:${a.skala}` : 'bez skali', arkusz: `ark. ${i + 1} z ${arkusze.length}`,
      opracowal: '', data, sprawdzil: '', nrTeczki,
    }
  })
  return arkusze
}

const rysujArkusz = (doc, font, biblioteki, form, a, indeks) => {
  const { uklad } = a
  const strona = doc.addPage([uklad.format.szer * PT, uklad.format.wys * PT])
  const r = rysownik(strona, font, biblioteki)
  rysujRamke(r, uklad)
  a.rysujTresc(r)
  for (const l of a.lancuchyX || []) rysujLancuch(r, { os: 'x', polozenie: l.polozenie, naPapier: a.naPapierX, rozklad: l.rozklad, strona: a.stronaX })
  for (const l of a.lancuchyY || []) rysujLancuch(r, { os: 'y', polozenie: l.polozenie, naPapier: a.naPapierY, rozklad: l.rozklad, strona: 1 })
  const k = uklad.kolumna
  const dol = rysujLegende(r, k.x, k.y, k.w, a.legenda)
  rysujOpis(r, k.x, dol + 1.5, k.w, uklad.tabelka.y - 1.5, a.opis.naglowek, a.opis.wiersze)
  rysujTabelke(r, { form, font }, uklad.tabelka, a.tabelka, `arkusz${indeks + 1}`)
}

/**
 * Rysuje arkusze i zwraca bajty PDF. `biblioteki` = { PDFDocument, rgb, degrees } z pdf-lib i `fontkit`
 * (przekazywane, żeby test w node mógł użyć tych samych modułów), `czcionka` = bajty TTF z polskimi znakami.
 * Arkusze bez tabelki dostają numerację z `ponumerujArkusze`.
 */
export const narysujArkusze = async ({ tytul, arkusze }, { PDFDocument, rgb, degrees, fontkit }, czcionka) => {
  if (arkusze.some((a) => !a.tabelka)) ponumerujArkusze(arkusze)
  const doc = await PDFDocument.create()
  doc.registerFontkit(fontkit)
  doc.setTitle(tytul)
  // Pełna czcionka (bez podzbioru): w polach „Opracował” i „Sprawdził” wykonawca wpisze dowolne litery.
  const font = await doc.embedFont(czcionka, { subset: false })
  const form = doc.getForm()
  arkusze.forEach((a, i) => rysujArkusz(doc, font, { rgb, degrees }, form, a, i))
  form.updateFieldAppearances(font)
  return doc.save()
}

/** W przeglądarce: pdf-lib i fontkit ładowane dopiero przy generowaniu, czcionka z `public/osd/`. */
export const rysujArkusze = async (opis, urlCzcionki) => {
  const [{ PDFDocument, rgb, degrees }, fontkitModul, czcionka] = await Promise.all([
    import('pdf-lib'), import('@pdf-lib/fontkit'), fetch(urlCzcionki).then((r) => r.arrayBuffer()),
  ])
  return narysujArkusze(opis, { PDFDocument, rgb, degrees, fontkit: fontkitModul.default || fontkitModul }, czcionka)
}
